import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import useApi from '../useApi';
import { DOWNLOAD_QUERY_KEYS } from './keys';
import type { AssetDownloadAgreementsResponse } from '../../../../types/isomorphic/download.types';

interface UseDownloadAgreementsParams {
  assetId: string;
  assets: { id: string }[];
  user: { isAuthenticated?: boolean; isRemembered?: boolean } | undefined;
}

const useDownloadAgreements = ({
  assetId,
  assets,
  user,
}: UseDownloadAgreementsParams) => {
  const { downloadService } = useApi();

  const fetchDownloadAgreements =
    async (): Promise<AssetDownloadAgreementsResponse | null> => {
      if (assets.length <= 1 && (user?.isAuthenticated || user?.isRemembered)) {
        // Prevent making an API call if we're handling batch asset downloads.
        const result = await downloadService.getDownloadAgreements(assetId);
        return result;
      }
      return null;
    };

  const query = useSuspenseQuery<AssetDownloadAgreementsResponse | null>({
    queryKey: DOWNLOAD_QUERY_KEYS.agreements(assetId),
    queryFn: () => fetchDownloadAgreements(),
  });
  return { ...query, response: query.data };
};

export const useInvalidateDownloadAgreements = () => {
  const queryClient = useQueryClient();

  return (assetId: string) =>
    queryClient.invalidateQueries({
      queryKey: DOWNLOAD_QUERY_KEYS.agreements(assetId),
    });
};

export default useDownloadAgreements;
